import { Button, CombinedPopoverProps, Icon, IconButtonProps, Popover } from '@alto-avios/alto-ui'
import React, { CSSProperties, useCallback, useEffect, useId, useRef, useState } from 'react'
import styles from './Tooltip.module.scss'

type TooltipProps = {
  title?: string
  content: React.ReactElement<React.PropsWithChildren>
  closeButtonText?: string
  triggerContent?: React.ReactElement<React.PropsWithChildren>
  iconProps?: IconButtonProps['iconProps']
  maxWidth?: CSSProperties['maxWidth']
  placement?: CombinedPopoverProps['placement']
  hasArrow?: CombinedPopoverProps['hasArrow']
  isOpen?: boolean
  onOpen?: () => void
  onClose?: () => void
  trigger?: 'hover' | 'click'
}

export const Tooltip = ({
  content,
  iconProps,
  closeButtonText,
  triggerContent,
  maxWidth,
  placement,
  isOpen,
  onOpen,
  onClose,
  trigger = 'click',
  hasArrow = true,
  title,
}: TooltipProps) => {
  const tooltipId = useId()
  const tooltipContentId = `${tooltipId}-content`
  const triggerRef = useRef<HTMLButtonElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const restoreFocusTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [open, setOpen] = useState(isOpen ?? false)

  const toggle = useCallback((state?: boolean) => {
    setOpen((prev) => state ?? !prev)
  }, [])

  const handleClose = useCallback(() => {
    if (!open) {
      return
    }

    toggle(false)
    onClose?.()

    if (trigger === 'hover') {
      return
    }

    if (restoreFocusTimeoutRef.current) {
      clearTimeout(restoreFocusTimeoutRef.current)
    }

    restoreFocusTimeoutRef.current = setTimeout(() => {
      triggerRef.current?.focus()
      restoreFocusTimeoutRef.current = null
    }, 0)
  }, [onClose, open, toggle, trigger])

  const handleOpen = useCallback(() => {
    if (open) {
      return
    }

    toggle(true)
    onOpen?.()
  }, [onOpen, open, toggle])

  const handleToggle = useCallback(() => {
    if (open) {
      handleClose()
      return
    }

    handleOpen()
  }, [handleClose, handleOpen, open])

  useEffect(() => {
    if (isOpen !== undefined) {
      setOpen(isOpen)
    }
  }, [isOpen])

  useEffect(() => {
    return () => {
      if (restoreFocusTimeoutRef.current) {
        clearTimeout(restoreFocusTimeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (!open) {
      return undefined
    }

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      if (!triggerRef.current?.contains(target) && !popoverRef.current?.contains(target)) {
        handleClose()
      }
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscape)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [open, handleClose])

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onMouseEnter={trigger === 'hover' ? handleOpen : undefined}
        onMouseLeave={trigger === 'hover' ? handleClose : undefined}
        onFocus={trigger === 'hover' ? handleOpen : undefined}
        onBlur={
          trigger === 'hover'
            ? (event) => {
                const nextFocused = event.relatedTarget as Node | null
                if (nextFocused && popoverRef.current?.contains(nextFocused)) {
                  return
                }
                handleClose()
              }
            : undefined
        }
        onClick={trigger === 'click' ? handleToggle : undefined}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            handleToggle()
          }

          if (e.key === 'Escape') {
            e.preventDefault()
            handleClose()
          }
        }}
        className={styles.trigger}
        title={title}
        aria-label={title}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={tooltipContentId}
      >
        {iconProps?.iconName ? (
          <Icon
            aria-hidden="true"
            iconName={iconProps.iconName}
            iconSize={iconProps.iconSize}
            iconProps={iconProps}
            color={iconProps.color}
            styleVariant="white"
          />
        ) : (
          triggerContent
        )}
      </button>

      {open ? (
        <Popover
          state={{
            close: handleClose,
            isOpen: open,
            open: handleOpen,
            toggle,
            setOpen: (value) => {
              if (value) {
                handleOpen()
                return
              }

              handleClose()
            },
          }}
          placement={placement}
          hasArrow={hasArrow}
          triggerRef={triggerRef as CombinedPopoverProps['triggerRef']}
          isNonModal
          autoFocus={false}
        >
          <div
            ref={popoverRef}
            id={tooltipContentId}
            className={styles.popoverContent}
            role="dialog"
            aria-modal="false"
            aria-label={title}
            tabIndex={-1}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus={trigger !== 'hover'}
            style={{ maxWidth }}
          >
            <div className={styles.popoverBody}>{content}</div>
            {closeButtonText && (
              <Button onPress={handleClose} styleVariant="accent" emphasis="secondary" size="sm">
                {closeButtonText}
              </Button>
            )}
          </div>
        </Popover>
      ) : null}
    </>
  )
}
